<script setup>
    import { useI18n } from 'vue-i18n';
    import { computed, onMounted, ref } from 'vue';
    import DccsForm from '../DccsForm/DccsForm.vue';
    import DccsHiddenField from '../DccsForm/components/DccsHiddenField/DccsHiddenField.vue';
    import DccsSelectField from '../DccsForm/components/DccsSelectField/DccsSelectField.vue';
    import DccsInputField from '../DccsForm/components/DccsInputField/DccsInputField.vue';
    import DccsTextarea from '../DccsForm/components/DccsTextarea/DccsTextarea.vue';
    import DccsCheckbox from '../DccsForm/components/DccsCheckbox/DccsCheckbox.vue';
    import DccsEmailInputField from '../DccsForm/components/DccsEmailInputField/DccsEmailInputField.vue';
    import DccsCountrySelectField from '../DccsForm/components/DccsCountrySelectField/DccsCountrySelectField.vue';
    import forEach from 'lodash-es/forEach';

    const { t } = useI18n();

    const traditionalLampsLink = 'https://www.ledvance.com/consumer/company/about-ledvance/contact';
    const automotiveLink = 'https://ams-osram.com/products/lamps-systems/automotive-lamps';
    const entertainmentLink = 'https://ams-osram.com/products/lamps-systems/digital-solutions';
    const quotationLink =
        'https://ams-osram.com/about-us/locations-distribution/channel-partners/distribution-partners';

    const messagePlaceholder = ref('');

    const isDocumentSelected = ref(false);
    const isTechnicalSelected = ref(false);

    const isPhotodetectorsSelected = ref(false);
    const isLaserSelected = ref(false);
    const isLedSelected = ref(false);
    const isSensorsSelected = ref(false);
    const isInterfacesSelected = ref(false);

    const props = defineProps({
        firstNamePlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        lastNamePlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        emailPlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        phonePlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        companyPlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        addressPlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        preselectedCountry: {
            type: Text,
            default: '',
            required: true,
        },
        postalCodePlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        cityPlaceholder: {
            type: Text,
            default: '',
            required: true,
        },
        liferayRestApi: {
            type: String,
            default: '',
        },
        trackingTitle: {
            type: String,
            default: '',
            required: true,
        },
        urlValue: {
            type: String,
            default: '',
            required: true,
        },
        orgidValue: {
            type: String,
            default: '',
            required: true,
        },
        recordTypeValue: {
            type: String,
            default: '',
            required: true,
        },
        subjectValue: {
            type: String,
            default: '',
            required: true,
        },
        sourceCampaignValue: {
            type: String,
            default: '',
            required: true,
        },
    });

    const preselectedCategory = computed(() => {
        let prefilter = location.search.replaceAll('?', '').split('&');
        let preselected = '';
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'category') {
                preselected = filterElement[1];
            }
        });
        return preselected;
    });

    const mainCategoryClass = computed(() => {
        let prefilter = location.search.replaceAll('?', '').split('&');
        let preselectedTopic = '';
        let preselectedCategoryElement = '';
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'topic') {
                preselectedTopic = filterElement[1];
            } else if (filterElement[0] === 'category') {
                preselectedCategoryElement = filterElement[1];
            }
        });
        if (preselectedTopic !== '' && preselectedTopic == 'semiconductor-and-automotive') {
            return 'to-show is-hidden';
        } else if (
            preselectedCategoryElement !== '' &&
            (preselectedCategoryElement == 'document-feedback' || preselectedCategoryElement == 'sample-request')
        ) {
            return 'to-show is-hidden';
        } else {
            return 'is-hidden';
        }
    });

    const showSubmitButton = computed(() => {
        let prefilter = location.search.replaceAll('?', '').split('&');
        let preselectedTopic = '';
        let preselectedCategoryElement = '';
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'topic') {
                preselectedTopic = filterElement[1];
            } else if (filterElement[0] === 'category') {
                preselectedCategoryElement = filterElement[1];
            }
        });

        if (preselectedTopic !== '' && preselectedTopic == 'semiconductor-and-automotive') {
            return true;
        } else if (preselectedCategoryElement !== '') {
            return true;
        } else {
            return false;
        }
    });

    const preselectedTopCategory = computed(() => {
        let prefilter = location.search.replaceAll('?', '').split('&');
        let preselectedTopic = '';
        let preselectedCategoryElement = '';
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'topic') {
                preselectedTopic = filterElement[1];
            } else if (filterElement[0] === 'category') {
                preselectedCategoryElement = filterElement[1];
            }
        });

        if (preselectedTopic !== '' && preselectedTopic == 'semiconductor-and-automotive') {
            return 'semi-context';
        } else if (preselectedCategoryElement !== '') {
            return 'semi-context';
        } else {
            return preselectedTopic;
        }
    });

    const preFilledDescription = computed(() => {
        let messagePlaceholder = '';
        let prefilter = location.search.replaceAll('?', '').split('&');
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'productHeader') {
                messagePlaceholder = messagePlaceholder + t('shop_product') + ': ' + filterElement[1] + '; ';
            } else if (filterElement[0] === 'productType') {
                messagePlaceholder = messagePlaceholder + t('search_productType') + ': ' + filterElement[1] + '; ';
            }
        });
        return messagePlaceholder;
    });

    const filterChanged = $filterValue => {
        if ($filterValue !== null && $filterValue !== undefined) {
            document.querySelectorAll('.filter-result').forEach($div => {
                if ($div.classList.contains($filterValue) && $div.classList.contains('is-hidden')) {
                    $div.classList.remove('is-hidden');
                } else if (!$div.classList.contains('is-hidden')) {
                    $div.classList.add('is-hidden');
                }
            });
            const allowedTypes = ['semi-context'];
            const $submitContainer = document.querySelectorAll('.dccs-form-actions');
            if ($submitContainer.length > 0) {
                if (allowedTypes.includes($filterValue)) {
                    $submitContainer[0].classList.remove('is-hidden');
                } else {
                    $submitContainer[0].classList.add('is-hidden');
                }
            }
        }
    };

    const categoryChanged = $categoryValue => {
        const $categoryDivs = document.querySelectorAll('.main-category > div.subcategory');
        $categoryDivs.forEach($div => {
            if ($categoryValue !== null && $categoryValue !== undefined) {
                if (
                    !$div.classList.contains('is-hidden') &&
                    !$div.classList.contains('category') &&
                    !(
                        $div.classList.contains('subcategory') &&
                        $div.classList.contains('document-feedback') &&
                        $categoryValue == 'document-feedback'
                    )
                ) {
                    $div.classList.add('is-hidden');
                    $div.querySelectorAll('input, select').forEach($input => {
                        if ($input.classList.contains('normal-validate')) {
                            $input.classList.remove('normal-validate');
                        }
                        $input.classList.add('no-validate');
                        $input.value = null;
                    });
                }

                if ($div.classList.contains($categoryValue.toLowerCase())) {
                    $div.classList.remove('is-hidden');
                    $div.querySelectorAll('input, select').forEach($input => {
                        $input.value = null;
                        if ($input.classList.contains('no-validate')) {
                            $input.classList.remove('no-validate');
                        }
                        $input.classList.add('normal-validate');
                    });
                }
            }
        });

        const $category = document.querySelector('.main-category select[name="WebRequestType__c"]');
        if ($category !== null && $category !== undefined && $category.value === 'datasheet-request') {
            isDocumentSelected.value = true;
            isTechnicalSelected.value = false;
        } else if ($category !== null && $category !== undefined && $category.value === 'technical-question') {
            isTechnicalSelected.value = true;
            isDocumentSelected.value = false;
        } else {
            isDocumentSelected.value = false;
            isTechnicalSelected.value = false;
        }

        const $area = document.querySelector('.main-category select[name="ProductArea__c"]');
        if ($area !== null && $area !== undefined && $area.value === 'Photodetectors') {
            isPhotodetectorsSelected.value = true;
            isLaserSelected.value = false;
            isLedSelected.value = false;
            isSensorsSelected.value = false;
            isInterfacesSelected.value = false;
        } else if ($area !== null && $area !== undefined && $area.value === 'laser') {
            isLaserSelected.value = true;
            isPhotodetectorsSelected.value = false;
            isLedSelected.value = false;
            isSensorsSelected.value = false;
            isInterfacesSelected.value = false;
        } else if ($area !== null && $area !== undefined && $area.value === 'LED') {
            isLedSelected.value = true;
            isPhotodetectorsSelected.value = false;
            isLaserSelected.value = false;
            isSensorsSelected.value = false;
            isInterfacesSelected.value = false;
        } else if ($area !== null && $area !== undefined && $area.value === 'Sensors') {
            isSensorsSelected.value = true;
            isPhotodetectorsSelected.value = false;
            isLaserSelected.value = false;
            isLedSelected.value = false;
            isInterfacesSelected.value = false;
        } else if ($area !== null && $area !== undefined && $area.value === 'Interfaces') {
            isInterfacesSelected.value = true;
            isPhotodetectorsSelected.value = false;
            isLaserSelected.value = false;
            isLedSelected.value = false;
            isSensorsSelected.value = false;
        } else {
            isPhotodetectorsSelected.value = false;
            isLaserSelected.value = false;
            isLedSelected.value = false;
            isSensorsSelected.value = false;
            isInterfacesSelected.value = false;
        }
    };

    const getSiteGroupId = computed(() => {
        return window.Liferay ? window.Liferay?.ThemeDisplay?.getSiteGroupId() : null;
    });

    onMounted(() => {
        let prefilter = location.search.replaceAll('?', '').split('&');
        forEach(prefilter, filter => {
            let filterElement = filter.split('=');
            if (filterElement[0] === 'topic') {
                let topicValue = '';
                if (filterElement[1] !== '') {
                    if (filterElement[1] == 'semiconductor-and-automotive') {
                        topicValue = 'semi-context';
                    } else {
                        topicValue = filterElement[1];
                    }
                    filterChanged(topicValue);
                }
            } else if (filterElement[0] === 'category') {
                filterChanged('semi-context');
                categoryChanged(filterElement[1]);
            } else if (filterElement[0] === 'productHeader') {
                messagePlaceholder.value =
                    messagePlaceholder.value + t('shop_product') + ': ' + filterElement[1] + '; ';
            } else if (filterElement[0] === 'productType') {
                messagePlaceholder.value =
                    messagePlaceholder.value + t('search_productType') + ': ' + filterElement[1] + '; ';
            }
        });

        document.querySelectorAll('.support-form .dccs-form-fields').forEach($container => {
            const $thirdPartyCheck = $container.querySelectorAll('.thirdPartyCheckbox');

            if ($thirdPartyCheck) {
                const $thirdPartyDiv = document.querySelectorAll('.thirdPartyCheck');
                $thirdPartyDiv.forEach($div => {
                    $div.querySelectorAll('input, select').forEach($input => {
                        if ($input.classList.contains('normal-validate')) {
                            $input.classList.remove('normal-validate');
                        }
                        $input.classList.add('no-validate');
                        $input.value = null;
                    });
                });
                $thirdPartyCheck.forEach($checkbox =>
                    $checkbox.addEventListener('change', () => {
                        if ($checkbox.getAttribute('value') === 'true') {
                            //open 3rd party requestor section
                            document.querySelector('.thirdPartyCheck').classList.remove('is-hidden');

                            $thirdPartyDiv.forEach($div => {
                                $div.querySelectorAll('input, select').forEach($input => {
                                    if ($input.classList.contains('no-validate')) {
                                        $input.classList.remove('no-validate');
                                    }
                                    $input.classList.add('normal-validate');
                                    $input.value = null;
                                });
                            });
                        } else {
                            // hide 3rd party requestor section
                            document.querySelector('.thirdPartyCheck').classList.add('is-hidden');
                            $thirdPartyDiv.forEach($div => {
                                $div.querySelectorAll('input, select').forEach($input => {
                                    if ($input.classList.contains('normal-validate')) {
                                        $input.classList.remove('normal-validate');
                                    }
                                    $input.classList.add('no-validate');
                                    $input.value = null;
                                });
                            });
                        }
                    })
                );
            }

            const $parentContainer =
                $container.parentNode !== null &&
                $container.parentNode !== undefined &&
                $container.parentNode.parentNode !== null &&
                $container.parentNode.parentNode !== undefined &&
                $container.parentNode.parentNode.parentNode !== null &&
                $container.parentNode.parentNode.parentNode !== undefined
                    ? $container.parentNode.parentNode.parentNode
                    : null;

            const $loadingInfo = $parentContainer.parentNode.querySelector('.form-is-loading');

            if ($parentContainer.classList.contains('is-hidden')) {
                $parentContainer.classList.remove('is-hidden');
                $loadingInfo.remove();
            }
        });
    });
</script>

<template>
    <div class="container support">
        <p class="form-is-loading">
            {{ t('form_loading') }}
        </p>

        <div class="dccs-form-wrapper is-hidden">
            <div class="dccs-form">
                <DccsForm
                    :action="liferayRestApi"
                    :button-label="t('form_submit')"
                    :submit-success-msg="t('supportForm_submitSuccessMsg')"
                    :submit-error-msg="t('supportForm_submitErrorMsg')"
                    :show-submit-button="showSubmitButton"
                    form-title="SupportForm"
                    :tracking-title="trackingTitle"
                    :url-value="urlValue"
                    class="support-form"
                >
                    <DccsHiddenField name="orgid" :value="orgidValue"></DccsHiddenField>
                    <DccsHiddenField name="RecordTypeId" :value="recordTypeValue"></DccsHiddenField>
                    <DccsHiddenField name="Subject" :value="subjectValue"></DccsHiddenField>
                    <DccsHiddenField name="SourceCampaign__c" :value="sourceCampaignValue"></DccsHiddenField>
                    <DccsHiddenField name="formId" value="7499914"></DccsHiddenField>
                    <DccsHiddenField name="groupId" :value="getSiteGroupId"></DccsHiddenField>

                    <DccsSelectField
                        type="select"
                        :label="t('supportForm_filterLabel')"
                        :required="true"
                        name="WebFilterType__c"
                        :options="t('supportForm_filterOptionsValue')"
                        class="filter"
                        :preselected="preselectedTopCategory"
                        @change="filterChanged($event.currentTarget.value)"
                    ></DccsSelectField>

                    <div class="semi-context main-category filter-result" :class="mainCategoryClass">
                        <DccsSelectField
                            type="select"
                            :label="t('supportForm_requestLabel')"
                            :required="true"
                            name="WebRequestType__c"
                            :options="t('supportForm_requestOptionsValue')"
                            class="category"
                            :preselected="preselectedCategory"
                            @change="categoryChanged($event.currentTarget.value)"
                        ></DccsSelectField>

                        <div v-if="isTechnicalSelected" class="technical-question subcategory">
                            <div class="column">
                                <p
                                    v-html="
                                        t('supportForm.technicalInfo_Text', {
                                            link: `<a href='/support'>${t('supportForm.technicalInfo_LinkText')}</a>`,
                                        })
                                    "
                                ></p>
                            </div>
                        </div>

                        <div v-if="isDocumentSelected" class="datasheet-request subcategory">
                            <div class="column">
                                <p>
                                    {{ t('supportForm_datasheetInfo_One') }}
                                    <a href="/support/download-center">{{ t('supportForm_datasheetInfo_Link') }}</a>
                                    {{ t('supportForm_datasheetInfo_Two') }}
                                </p>
                            </div>

                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_documentLabel')"
                                    :required="isDocumentSelected"
                                    name="DocumentType__c"
                                    :options="t('supportForm_documentOptionsValue')"
                                ></DccsSelectField>
                            </div>
                            <div class="columns">
                                <div class="column">
                                    <DccsInputField
                                        type="text"
                                        :label="t('supportForm_documentVersionLabel')"
                                        name="DocumentVersion__c"
                                        :required="isDocumentSelected"
                                    ></DccsInputField>
                                </div>
                                <div class="column">
                                    <DccsInputField
                                        type="text"
                                        :label="t('supportForm_documentNumberLabel')"
                                        :required="false"
                                        name="DocumentNumber__c"
                                    ></DccsInputField>
                                </div>
                                <div class="column">
                                    <DccsInputField
                                        type="text"
                                        :label="t('supportForm_documentTitleLabel')"
                                        name="DocumentTitle__c"
                                        :required="false"
                                    ></DccsInputField>
                                </div>
                                <div class="column">
                                    <DccsInputField
                                        type="text"
                                        :label="t('supportForm_partNumberLabel')"
                                        name="PartNumber__c"
                                        :required="false"
                                    ></DccsInputField>
                                </div>
                            </div>
                        </div>

                        <DccsSelectField
                            type="select"
                            :label="t('supportForm_applicationLabel')"
                            :required="true"
                            name="Application__c"
                            :options="t('supportForm_applicationOptionsValue')"
                        ></DccsSelectField>

                        <DccsSelectField
                            type="select"
                            :label="t('supportForm_productAreasLabel')"
                            :required="true"
                            name="ProductArea__c"
                            :options="t('supportForm_productAreasOptionsValue')"
                            class="category"
                            @change="categoryChanged($event.currentTarget.value)"
                        ></DccsSelectField>
                        <div v-if="isLedSelected" class="led subcategory">
                            <div class="column">
                                <DccsSelectField
                                    :key="ledTypeKey"
                                    type="select"
                                    :label="t('supportForm_ledTypesLabel')"
                                    :required="isLedSelected"
                                    name="ledTypes__c"
                                    :options="t('supportForm_ledTypesOptionsValue')"
                                    class="no-validate"
                                ></DccsSelectField>
                            </div>
                        </div>
                        <div v-if="isLaserSelected" class="laser subcategory">
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_laserTypesLabel')"
                                    :required="isLaserSelected"
                                    name="laserType__c"
                                    :options="t('supportForm_laserTypesOptionsValue')"
                                    class="no-validate"
                                ></DccsSelectField>
                            </div>
                        </div>
                        <div v-if="isPhotodetectorsSelected" class="photodetectors subcategory">
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_photodetectorsTypesLabel')"
                                    :required="isPhotodetectorsSelected"
                                    name="photodetectorsType__c"
                                    :options="t('supportForm_photodetectorsTypesOptionsValue')"
                                    class="no-validate"
                                ></DccsSelectField>
                            </div>
                        </div>
                        <div v-if="isSensorsSelected" class="sensors subcategory">
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_sensorsTypesLabel')"
                                    :required="isSensorsSelected"
                                    name="sensorsType__c"
                                    :options="t('supportForm_sensorsTypesOptionsValue')"
                                    class="no-validate"
                                ></DccsSelectField>
                            </div>
                        </div>
                        <div v-if="isInterfacesSelected" class="interfaces subcategory">
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_interfacesTypesLabel')"
                                    :required="isInterfacesSelected"
                                    name="interfacesType__c"
                                    :options="t('supportForm_interfacesTypesOptionsValue')"
                                    class="no-validate"
                                ></DccsSelectField>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_preferredDistributorLabel')"
                                    :required="true"
                                    name="PreferredDistributor__c"
                                    :options="t('supportForm_preferredDistributorOptionsValue')"
                                ></DccsSelectField>
                            </div>
                            <div class="column">
                                <DccsSelectField
                                    type="select"
                                    :label="t('supportForm_departmentLabel')"
                                    :required="true"
                                    name="WebDepartment__c"
                                    :options="t('supportForm_departmentOptionsValue')"
                                ></DccsSelectField>
                            </div>
                        </div>

                        <DccsSelectField
                            type="select"
                            :label="t('supportForm_anredeLabel')"
                            :required="true"
                            name="WebSalutation__c"
                            :options="t('supportForm_anredeOptionsValue')"
                        ></DccsSelectField>

                        <div class="columns">
                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.firstName')"
                                    name="WebFirstName__c"
                                    :required="true"
                                    :placeholder="firstNamePlaceholder"
                                    :value="firstNamePlaceholder"
                                ></DccsInputField>
                            </div>
                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.familyName')"
                                    name="WebLastName__c"
                                    :required="true"
                                    :placeholder="lastNamePlaceholder"
                                    :value="lastNamePlaceholder"
                                ></DccsInputField>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <DccsEmailInputField
                                    :is-required="true"
                                    :required="true"
                                    :field-label="t('dashboard.eMail')"
                                    :field-placeholder="emailPlaceholder"
                                    :field-value="emailPlaceholder"
                                    field-name="SuppliedEmail"
                                ></DccsEmailInputField>
                            </div>
                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.phone')"
                                    name="SuppliedPhone"
                                    pattern="^[^A-Za-z]*$"
                                    :required="false"
                                    :placeholder="phonePlaceholder"
                                    :value="phonePlaceholder"
                                    :error-message="t('errorMessage.phonePattern')"
                                ></DccsInputField>
                            </div>
                        </div>

                        <DccsInputField
                            type="text"
                            :label="t('dashboard.company')"
                            name="SuppliedCompany"
                            :required="true"
                            :placeholder="companyPlaceholder"
                            :value="companyPlaceholder"
                            :maxlength="80"
                        ></DccsInputField>

                        <div class="columns">
                            <div class="column">
                                <DccsCountrySelectField
                                    field-name="WebCountry__c"
                                    :is-required="true"
                                    :field-label="t('dashboard.country')"
                                    :preselected-country="preselectedCountry"
                                ></DccsCountrySelectField>
                            </div>

                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.streetAddress')"
                                    name="WebStreet__c"
                                    :required="false"
                                    :placeholder="addressPlaceholder"
                                    :value="addressPlaceholder"
                                ></DccsInputField>
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.postalCode')"
                                    name="WebPostalCode__c"
                                    :required="true"
                                    :placeholder="postalCodePlaceholder"
                                    :value="postalCodePlaceholder"
                                ></DccsInputField>
                            </div>

                            <div class="column">
                                <DccsInputField
                                    type="text"
                                    :label="t('dashboard.city')"
                                    name="City"
                                    :required="true"
                                    :placeholder="cityPlaceholder"
                                    :value="cityPlaceholder"
                                ></DccsInputField>
                            </div>
                        </div>

                        <DccsTextarea
                            :label="t('supportForm_yourMessage')"
                            :required="true"
                            name="Description"
                            :rows="3"
                            :placeholder="messagePlaceholder.value"
                            :value="preFilledDescription"
                        ></DccsTextarea>

                        <p class="datenschutz-intro">
                            {{ t('supportForm_datenschutzIntro_Text') }}
                            <a href="/privacy-policy">{{ t('supportForm_datenschutzIntro_Link') }}</a>
                        </p>

                        <DccsCheckbox
                            :label="t('supportForm_datenschutzText')"
                            name="TnCsaccepted__c"
                            :required="true"
                            :error-message="t('supportForm_datenschutzError')"
                        >
                            {{ t('supportForm_datenschutzText') }}
                        </DccsCheckbox>

                        <DccsCheckbox
                            :label="t('supportForm_marketingText')"
                            name="WebEmailPermission__c"
                            :required="false"
                            :error-message="t('supportForm_marketingError')"
                        >
                            {{ t('supportForm_marketingText') }}
                        </DccsCheckbox>

                        <p class="form-fields-note">
                            <em>{{ t('supportForm_formFieldsNote') }}</em>
                        </p>
                    </div>

                    <div class="container button-container is-centered filter-result traditional-lamps-bulbs is-hidden">
                        <a :href="traditionalLampsLink" class="button is-primary" target="_blank">
                            {{ t('form.click.here') }}
                        </a>
                    </div>

                    <div class="container button-container is-centered filter-result automotive is-hidden">
                        <a :href="automotiveLink" class="button is-primary" target="_blank">
                            {{ t('form.click.here') }}
                        </a>
                    </div>
                    <div class="container button-container is-centered filter-result entertainment is-hidden">
                        <a :href="entertainmentLink" class="button is-primary" target="_blank">
                            {{ t('form.click.here') }}
                        </a>
                    </div>
                    <div class="container button-container is-centered filter-result quotation is-hidden">
                        <a :href="quotationLink" class="button is-primary" target="_blank">
                            {{ t('form.click.here') }}
                        </a>
                    </div>
                </DccsForm>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import '../../../scss/variables';
    @import '../../../scss/utilities/all';
    @import '../../../scss/elements/title';
</style>
